import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { TPODataContext } from 'contexts/TPOContext';
import { Button, MenuProps, Spin, Switch, Tag, Tooltip } from 'antd';
import { CustomSelect } from '../PricingSimulator/DimSection';
import { CustomDropdownButton } from '../PricingSimulator/ScenarioActions';
import { EyeOutlined, EyeInvisibleOutlined, PlusCircleOutlined, ProjectOutlined } from '@ant-design/icons';
import {
    compareUsers,
    downloadCsv,
    downloadXlsx,
    generateErrorMsg,
    getConfig,
    sendNotification,
    transformFiltersV2,
} from 'utils/helperMethods';
import OptimizePlanModal from './OptimizePlanModal/OptimizePlan';
import BtnDropdown from '../common/BtnDropdown';
import {
    addEventDropdownList,
    addEventDropdownListEventLibrary,
} from 'components/organism/TradePromoOptimization/UpdateOrViewTPO';
import ConfirmationModal from '../common/modals/ConfirmationModal';
import { LoadingOutlined } from '@ant-design/icons';
import OptimizeResultModal from './OptimizeResultModal/OptimizeResult';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import KPISelector from './AddEventModal/PromoSections/KPISelector';
import axios from 'axios';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    OPTIMIZATION_POLLING_INTERVAL,
    MANDATORY_NON_CALENDAR_COLUMN_DATA_KEYS,
    INFEASIBLE_SOLUTION_KEYWORDS,
} from 'constants/tpo';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import SearchBox from '../SearchBox';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { antIcon } from '../Filter/FilterApplyButton';
import {
    OptimizationPollingStatus,
    InfeasibilityPollingStatus,
    ValidatePlanPollingStatus,
} from 'types/tradePromoOptimization';
import TPODuplicatePlanModal from '../PricingSimulator/Modals/TPOCopyPlanModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { UserContext } from 'contexts/ConfigContext';

dayjs.extend(utc);
dayjs.extend(timezone);
import { queryClient } from 'services/reactQueryService';
import ValidatePlanDrawer from './Modals/ValidatePlanDrawer';
import { ValidatePlanSection } from 'types/tpo/validatePlan';
import { parseValidatePlanResponse } from 'parser/tradePromoOptimization/validatePlanParser';
import { trackEventMixpanel } from 'utils/mixpanel';
import { TPO_PLAN_DETAILS_SUB_VIEW_CHANGED } from 'utils/mixpanelEventNames';

export const timePeriodDropdownList = [
    { label: 'FULL YEAR', value: 'full_year' },
    { label: 'YTD', value: 'ytd' },
    { label: 'YTG', value: 'ytg' },
];

const export_calender_view = 'export_calender_view';
const save_plan = 'save_plan';
const duplicate_plan = 'duplicate_plan';
const reset_plan = 'reset_plan';
const reset_library = 'reset_library';
const show_changes = 'show_changes';
const sync_with_tpm = 'sync_with_tpm';
const delete_plan = 'delete_plan';
const delete_all_promos_events = 'delete_all_promos_events';
const optimization_results = 'optimization_results';
const validate_plan = 'validate_plan';
const validate_plan_result = 'validate_plan_result';
const optimization_plan_view = 'optimization_plan_view';
const set_to_original = 'set_to_original';
const change_kpi_view = 'change_kpi_view';
const edit_plan = 'edit_plan';
const publish_plan = 'publish_plan';
const send_for_approval = 'send_for_approval';
const export_unpublished = 'export_unpublished';

/** Returns true if current time in ET falls within the TPM batch window (e.g. 8:30 PM–7:00 AM ET). */
function isInTpmBatchWindow(startET: string, endET: string): boolean {
    const start = startET;
    const end = endET;
    const et = dayjs().tz('America/New_York');
    const nowMinutes = et.hour() * 60 + et.minute();
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    const windowStart = startH * 60 + startM;
    const windowEnd = endH * 60 + endM;
    // Window spans midnight (e.g. 20:30–07:00): in window if after start OR before end
    if (windowStart > windowEnd) {
        return nowMinutes >= windowStart || nowMinutes < windowEnd;
    }
    return nowMinutes >= windowStart && nowMinutes < windowEnd;
}

type StatusInfo = {
    approval_status: string | null;
    approval_request_ts: string | null;
    approval_log: string | null;
    publish_status: string | null;
    publish_time: string | null;
};

type Props = {
    addEventButtonClickHandler: Function;
    setOpenSyncWithTpmConfirmationModal: Function;
    setOpenSavePlanConfirmationModal: Function;
    kpiVariable: any;
    onDropdownChange: (variables: any) => void;
    onViewDropdownChange: Function;
    disableAddEvent?: boolean;
    onSearch: (val: string) => void;
    setCalenderVariable: Function;
    setOpenOptimizationSyncConfirmationModal: Function;
    setOpenKpiViewPopup: Function;
    filterComponent: React.ReactNode;
    onClickPublishPlan: Function;
    onSendForApproval: Function;
    calenderStatues: StatusInfo | undefined;
    updateFilterObject: Function;
    setKpiVariable: Function;
    selectedCalendarColumns?: string[];
    setSelectedCalendarColumns?: Function;
    calenderDataLoading: boolean;
    applyFilterObject?: any;
    availableFilters?: any[];
};

const PlanActions: FC<Props> = ({
    addEventButtonClickHandler,
    setOpenSyncWithTpmConfirmationModal,
    setOpenSavePlanConfirmationModal,
    kpiVariable,
    onDropdownChange,
    onViewDropdownChange,
    onSearch,
    setCalenderVariable,
    disableAddEvent = false,
    setOpenOptimizationSyncConfirmationModal,
    setOpenKpiViewPopup,
    filterComponent,
    onClickPublishPlan,
    onSendForApproval,
    calenderStatues,
    updateFilterObject,
    setKpiVariable,
    selectedCalendarColumns = [],
    setSelectedCalendarColumns,
    calenderDataLoading = false,
    applyFilterObject = {},
    availableFilters = [],
}) => {
    const {
        selectedPlanInfo,
        setEventViewType,
        eventViewType,
        enableBumpReport,
        updatePlanProperty,
        setEnableBumpReport,
        dimensionConfigData,
        showCalenderChanges,
        kpiConfigData,
        setShowCalenderChanges,
        isTpmTab,
        tpmTpoActiveTab,
        planMetaDataLoading,
        optimizedPlanId,
        setOptimizedPlanId,
        setShowOptimizedPlanCalender,
        showOptimizedPlanCalender,
        isCurrentViewEventLibrary,
        setIsKpiAbsView,
        isKpiAbsView,
        viewMode,
        planEditMetaData,
        setPlanEditMetaData,
        pollingStatus,
        isOptimizationInProgress,
        setPollingStatus,
        setOptimizationError,
        optimizationError,
        infeasibilityPollingStatus,
        setInfeasibilityPollingStatus,
        setInfeasibilityData,
        validatePlanId,
        setValidatePlanId,
        validatePlanPollingStatus,
        setValidatePlanPollingStatus,
        planSummarySubTab,
        kpiRawData,
    } = useContext(TPODataContext);

    const { appConfig } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const {
        calendarFilterKeys,
        planMetricsFilterKeys,
        promoMetricsFilterKeys,
        psProductViewFilterKeys,
        psTacticViewFilterKeys,
    } = tpoConfig;
    const tpmWindowStart = tpoConfig?.tpmBatchWindowStartET;
    const tpmWindowEnd = tpoConfig?.tpmBatchWindowEndET;
    const disablePublish = Boolean(tpoConfig?.disablePublish);
    const disablePlanApproval = Boolean(tpoConfig?.disablePlanApproval);
    const viewDropdownList = tpoConfig?.viewDropdownList ?? [];

    const [searchKey, setSearchKey] = useState('');
    const [selectedKpiTimePeriod, setSelectedKpiTimePeriod] = useState(timePeriodDropdownList?.[0]);
    const [openOptimizeModal, setOpenOptimizeModal] = useState<boolean>(false);
    const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState<boolean>(false);
    const [openEditPlanConfirmationModal, setOpenEditPlanConfirmationModal] = useState<boolean>(false);
    const [openOptimizeResultModal, setOpenOptimizeResultModal] = useState<boolean>(false);
    const [kpiFilterDropdownValues, setKpiDropdownValues] = useState<any[]>([]);
    const [isKpiFilterVisible, setIsKpiFilterVisible] = useState(false);
    const [dimensionDropdownValues, setDimensionDropdownValues] = useState<any>([]);
    const [isCopyModalVisible, setIsCopyModalVisible] = useState<boolean>(false);
    const [openDeleteAllConfirmationModal, setOpenDeleteAllConfirmationModal] = useState<boolean>(false);
    const [openValidatePlanDrawer, setOpenValidatePlanDrawer] = useState<boolean>(false);
    const [hasValidatePlanData, setHasValidatePlanData] = useState<boolean>(false);
    const [showValidatePlanLoader, setShowValidatePlanLoader] = useState<boolean>(false);

    const [validatePlanSections, setValidatePlanSections] = useState<ValidatePlanSection[]>([]);

    const [optimizationId, setOptimizationId] = useState<string>('');

    // Check if the optimization status title indicates an infeasible solution
    const isInfeasibleSolution =
        pollingStatus === OptimizationPollingStatus.COMPLETED &&
        INFEASIBLE_SOLUTION_KEYWORDS.some((keyword) =>
            optimizationError?.title?.toLowerCase()?.includes(keyword.toLowerCase()),
        );

    const pollingFn = async () => {
        try {
            const data = await axios.get(`${tradePromoOptimizationUrls.getOptimizationStatus}/${optimizationId}`);
            setPollingStatus(data.data.status);
            setOptimizedPlanId(data.data.optimized_plan_id);
            setOptimizationError(data.data?.optimization_status);
            if (data.data?.optimized_plan_id) {
                setShowOptimizedPlanCalender(true);
                setCalenderVariable({});
            }
        } catch (err: any) {
            setOptimizationError({ title: 'Optimization failed', message: err?.response?.data?.detail });
            setPollingStatus(OptimizationPollingStatus.ERROR);
            console.log(err);
            return err.message;
        }
    };

    const getInfeasibilityExplanationPollingFunction = async () => {
        try {
            const data = await axios.get(`${tradePromoOptimizationUrls.getInfeasibilityExplanation}/${optimizationId}`);
            if (data.data?.data?.relaxed_constraints) {
                setInfeasibilityPollingStatus(InfeasibilityPollingStatus.COMPLETED);
                setInfeasibilityData(data.data?.data?.relaxed_constraints || []);
                sendNotification(
                    'success',
                    'Diagnosing infeasibility constraints complete, access it from Infeasible Plan Factors button',
                );
            }

            return data.data;
        } catch (err: any) {
            setInfeasibilityPollingStatus(InfeasibilityPollingStatus.ERROR);
            throw err;
        }
    };

    const { isLoading } = useQuery({
        queryKey: [`get_optimization_status/${optimizationId}`],
        queryFn: pollingFn,
        refetchInterval:
            pollingStatus === OptimizationPollingStatus.IN_PROGRESS ? OPTIMIZATION_POLLING_INTERVAL : false, // Start polling only if `polling` is true
        enabled: !!optimizationId, // Ensure query only runs if `polling` is true
        staleTime: 0,
    });

    const { isLoading: isInfeasibilityExplanationLoading } = useQuery({
        queryKey: [`get_infeasibility_explanation/${optimizationId}`],
        queryFn: getInfeasibilityExplanationPollingFunction,
        refetchInterval:
            infeasibilityPollingStatus === InfeasibilityPollingStatus.IN_PROGRESS
                ? OPTIMIZATION_POLLING_INTERVAL
                : false,
        enabled: !!optimizationId && isInfeasibleSolution,
        staleTime: 0,
    });

    // Validate plan polling function
    const validatePlanPollingFn = async () => {
        try {
            const data = await axios.get(`${tradePromoOptimizationUrls.getValidatePlanResult}/${validatePlanId}`);
            setValidatePlanPollingStatus(data.data.status);
            if (data.data?.status === 'ERROR') {
                setValidatePlanPollingStatus(ValidatePlanPollingStatus.ERROR);
                setShowValidatePlanLoader(false);
            } else if (data.data?.ds_response) {
                const parsedSections = parseValidatePlanResponse(data.data.ds_response);
                setValidatePlanSections(parsedSections);
                setHasValidatePlanData(true);
                setShowValidatePlanLoader(false);
            }
        } catch (err: any) {
            setValidatePlanPollingStatus(ValidatePlanPollingStatus.ERROR);
            setShowValidatePlanLoader(false);
            console.log(err);
            return err.message;
        }
    };

    const { isLoading: isValidatePlanPolling } = useQuery({
        queryKey: [`get_validate_plan_result/${validatePlanId}`],
        queryFn: validatePlanPollingFn,
        refetchInterval:
            validatePlanPollingStatus === ValidatePlanPollingStatus.IN_PROGRESS ? OPTIMIZATION_POLLING_INTERVAL : false,
        enabled: !!validatePlanId,
        staleTime: 0,
    });

    const { mutate: exportCalenderFn, loading: exportCalenderLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.getExportDataUrl,
        method: 'post',
    });

    const { mutate: exportKpiFn, loading: exportKpiLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.getExportKpiViewUrl,
        method: 'post',
    });

    const { mutate: validatePlanFn, loading: validatePlanLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.validatePlanUrl,
        method: 'post',
    });

    const deleteAllPromotions = async (): Promise<any> => {
        if (deleteAllPromotionsMutation.isPending) return;
        const response = await axios.delete(`${tradePromoOptimizationUrls.deleteAllPromosEvents}`, {
            params: {
                plan_id: selectedPlanInfo?.planId,
                is_tpm_plan: selectedPlanInfo?.isTpmPlan,
            },
        });
        return response;
    };

    const acquireEditRights = async (payload: { planId: Number }): Promise<any> => {
        if (acquireEditRightsMutation.isPending) return;
        const user_id = localStorage.getItem('email');
        const response = await axios.post(
            `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/${payload.planId}/edit/acquire`,
            {
                user_id,
            },
        );
        return response;
    };

    const acquireEditRightsMutation = useMutation({
        mutationFn: acquireEditRights,
        onSuccess: (data: any) => {
            //if user is same, then it means that edit rights are acquired
            if (compareUsers(data?.data?.edit_lock?.editing_user)) {
                sendNotification('success', tpoNotificationMessages.PLAN_EDIT_ACQUIRE_SUCCESS);
            } else {
                sendNotification('error', `User ${data?.data?.edit_lock?.editing_user} is currently editing this plan`);
            }
            setPlanEditMetaData({
                editingUser: data?.data?.edit_lock?.editing_user,
                sessionExpiresInSecs: data?.data?.edit_lock?.session_expires_in_sec,
                startedAt: data?.data?.edit_lock?.started_at,
                planId: data?.data?.plan_id,
            });
            setOpenEditPlanConfirmationModal(false);
        },
        onError: (error: any) => {
            const errorMsg = generateErrorMsg(
                String(error?.response?.data?.detail || ''),
                ErrorMessages.GENERALIZED_ERROR_MSG,
            );
            //show error message
            sendNotification('error', errorMsg);
        },
    });

    const deleteAllPromotionsMutation = useMutation({
        mutationFn: deleteAllPromotions,
        onSuccess: (res: any) => {
            if (res.data.success) {
                console.log('deleting');
                queryClient.invalidateQueries({
                    queryKey: ['tpo-event-data', 'tpo-calender-data'],
                    exact: false,
                });
            } else {
                sendNotification('error', res.data.message);
            }
            setOpenDeleteAllConfirmationModal(false);
        },
        onError: (error: any) => {
            const errorMsg = generateErrorMsg(
                String(error?.response?.data?.detail || ''),
                ErrorMessages.GENERALIZED_ERROR_MSG,
            );
            //show error message
            sendNotification('error', errorMsg);
        },
    });

    const getOptimizationIdVariables = useMemo(
        () => ({
            plan_id: selectedPlanInfo?.planId,
            is_tpm_plan: selectedPlanInfo?.isTpmPlan,
            is_tpm_tab: isTpmTab,
        }),
        [selectedPlanInfo?.planId, selectedPlanInfo?.isTpmPlan, isTpmTab],
    );

    const { loading: getOptimizationIdLoading, error: getOptimizationIdError } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: !!getOptimizationIdVariables?.plan_id, cacheTime: 0, staleTime: 0 },
        url: tradePromoOptimizationUrls.getOptimizationId,
        queryKey: [`getOptimizationId_${selectedPlanInfo?.planId}`, getOptimizationIdVariables],
        variables: getOptimizationIdVariables,
        parserFunction: (res: any) => {
            if (res) setOptimizationId(res.optimization_id);
            return res;
        },
    });
    //export function for calender and kpi view
    function exportPlan(exportType: 'export' | 'export_unpublished' = 'export') {
        sendNotification('info', tpoNotificationMessages.PLAN_EXPORT_QUEUE);
        const payload = isCalenderView
            ? {
                  plan_name: selectedPlanInfo?.planName,
                  plan_id: showOptimizedPlanCalender ? optimizedPlanId : selectedPlanInfo?.planId, //
                  is_tpm_tab: isTpmTab,
                  is_tpm: selectedPlanInfo?.isTpmPlan,
                  is_optimized: showOptimizedPlanCalender,
                  export_unpublished: exportType === 'export_unpublished',
              }
            : {
                  ...kpiVariable,
                  offset: 0,
                  limit: 0,
              };
        const functionToCall = isCalenderView ? exportCalenderFn : exportKpiFn;
        functionToCall(
            { ...payload },
            {
                onSuccess: (data) => {
                    const fileName = `${selectedPlanInfo?.planName}`;
                    downloadXlsx(data?.data, fileName);
                    //  downloadCsv([data?.data], fileName);
                    sendNotification(
                        'success',
                        `${selectedPlanInfo?.planName} Export is ready! The download has started automatically`,
                        5,
                        500,
                    );
                },
                onError: (error: any) => {
                    const errorMsg = generateErrorMsg(
                        String(error?.response?.data?.detail || ''),
                        ErrorMessages.GENERALIZED_ERROR_MSG,
                    );
                    //show error message
                    sendNotification('error', errorMsg);
                },
            },
        );
    }

    // Validate plan function - triggers API and opens drawer with loader
    // Always resets data and shows loader when clicked
    function onValidatePlanClick() {
        // Reset data and state when validate plan is clicked again
        setValidatePlanSections([]);
        setHasValidatePlanData(false);
        setValidatePlanId('');

        // Always show loader when starting validation
        setShowValidatePlanLoader(true);
        setOpenValidatePlanDrawer(true);
        setValidatePlanPollingStatus(ValidatePlanPollingStatus.IN_PROGRESS);

        const transformedFilters = transformFiltersV2(applyFilterObject, availableFilters);
        if (selectedPlanInfo?.dim2Filter?.label && selectedPlanInfo?.dim2Filter?.value?.length > 0) {
            if (!transformedFilters.dim2) {
                transformedFilters.dim2 = { inclusion: [], exclusion: [] };
            }
            selectedPlanInfo.dim2Filter.value.forEach((value: string) => {
                transformedFilters.dim2.inclusion.push({
                    attributeType: selectedPlanInfo.dim2Filter.label,
                    attributeExId: value,
                    attributeDesc: value,
                    dimension: 'dim2',
                });
            });
        }

        const payload = {
            plan_id: selectedPlanInfo?.planId,
            filters: transformedFilters,
            is_tpm_plan: selectedPlanInfo?.isTpmPlan,
            asper_customer: process.env.REACT_APP_TENANT_ID,
        };
        validatePlanFn(payload, {
            onSuccess: (data: any) => {
                if (data?.data?.plan_id) {
                    setValidatePlanId(data.data.plan_id);
                }
            },
            onError: (error: any) => {
                const errorMsg = generateErrorMsg(
                    String(error?.response?.data?.detail || ''),
                    ErrorMessages.GENERALIZED_ERROR_MSG,
                );
                sendNotification('error', errorMsg);
                setValidatePlanPollingStatus(ValidatePlanPollingStatus.ERROR);
                setShowValidatePlanLoader(false);
            },
        });
    }

    // Close drawer without resetting polling state - API continues running in background
    const handleValidatePlanDrawerClose = () => {
        setOpenValidatePlanDrawer(false);
    };

    // Show validate plan result - opens drawer to display existing data (no loader)
    const onShowValidatePlanResultClick = () => {
        setShowValidatePlanLoader(false);
        setOpenValidatePlanDrawer(true);
    };

    useEffect(() => {
        if (kpiVariable && kpiVariable?.['kpi_list']?.length > 0 && kpiFilterDropdownValues?.length === 0) {
            const checkKey = isPlanSummaryView ? planSummarySubTab : eventViewType;
            const rawItems: any[] = kpiRawData ?? [];
            const list = rawItems
                .filter((item: any) => {
                    const availableFor: string[] = item?.available_for || [];
                    return availableFor.includes(checkKey);
                })
                .map((item: any) => {
                    return {
                        label:
                            eventViewType === 'promo_metrics' || eventViewType === 'plan_summary'
                                ? item?.label
                                : item?.plan_label,
                        value: item?.value,
                        isSelected: item?.show_by_default || false,
                    };
                });
            setKpiDropdownValues(list);
        }
    }, [kpiVariable, eventViewType, planSummarySubTab]);

    //syncing context search key with component
    useEffect(() => {
        if (selectedPlanInfo?.search !== searchKey) setSearchKey(selectedPlanInfo?.search || '');
    }, [selectedPlanInfo?.search]);

    //reset dropdown values when view dropdown changes
    useEffect(() => {
        return () => {
            setDimensionDropdownValues([]);
            setKpiDropdownValues([]);
            if (setSelectedCalendarColumns) {
                setSelectedCalendarColumns([]);
            }
        };
    }, [eventViewType, planSummarySubTab]);

    // Initialize calendar columns with default selections
    useEffect(() => {
        const isCalenderView = eventViewType === 'calender';

        if (
            isCalenderView &&
            dimensionConfigData?.calendarColumnDropdownList?.length &&
            selectedCalendarColumns?.length === 0
        ) {
            // Find mandatory column values
            const mandatoryColumnValues =
                dimensionConfigData?.calendarColumnDropdownList
                    ?.filter((item: any) => MANDATORY_NON_CALENDAR_COLUMN_DATA_KEYS.includes(item.dataKey))
                    ?.map((item: any) => item.value) || [];

            const defaultColumns = dimensionConfigData.calendarColumnDropdownList
                ?.filter((item: any) => item?.isDefault)
                ?.map((item: any) => item?.value);

            // Ensure mandatory columns are always included
            const columnsToSet = [...new Set([...mandatoryColumnValues, ...(defaultColumns || [])])];

            if (setSelectedCalendarColumns && columnsToSet?.length) {
                setSelectedCalendarColumns(columnsToSet);
            }
        }
    }, [
        dimensionConfigData?.calendarColumnDropdownList,
        eventViewType,
        selectedCalendarColumns?.length,
        setSelectedCalendarColumns,
    ]);

    //update the view type and dropdown values when view dropdown changes
    useEffect(() => {
        if (dimensionConfigData?.dimensionDropdownList?.length && dimensionDropdownValues?.length === 0) {
            const dimensionDropdownList = dimensionConfigData?.dimensionDropdownList
                ?.filter((item: any) => item?.isDefault)
                ?.map((item: any) => item?.value);
            setDimensionDropdownValues([...(dimensionDropdownList ?? [])]);
            onDropdownChange({
                ...kpiVariable,
                view_type: eventViewType === 'promo_metrics' ? 'promo' : 'plan',
                group_by: dimensionDropdownList,
            });
        }
    }, [dimensionConfigData?.dimensionDropdownList]);

    const onClickCancelOptimizePlan = () => {
        setOpenOptimizeModal(false);
    };

    const isYearExpired = selectedPlanInfo?.planYear && selectedPlanInfo?.planYear < dayjs().year();

    const isCalenderView = eventViewType === 'calender';
    const isPlanMetricView = eventViewType === 'plan_metrics';
    const isPlanSummaryView = eventViewType === 'plan_summary';
    const optionDropdownItems: MenuProps['items'] = useMemo(() => {
        const items: MenuProps['items'] = isCurrentViewEventLibrary
            ? [
                  {
                      disabled: true,
                      label: <div className={`font-xs  ${false ? 'cursor-not-allowed' : 'cursor-pointer'}`}>Save</div>,
                      key: save_plan,
                      onClick: () => setOpenSavePlanConfirmationModal(true),
                  },
                  {
                      disabled: true,
                      label: <div className={`font-xs  ${false ? 'cursor-not-allowed' : 'cursor-pointer'}`}>Reset</div>,
                      key: reset_library,
                      // onClick: () => setOpenSavePlanConfirmationModal(true),
                  },
                  {
                      type: 'divider' as const,
                  },
                  {
                      disabled: true,
                      label: (
                          <div className={`font-xs text-redAlt  ${false ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                              Delete Library
                          </div>
                      ),
                      key: delete_plan,
                      onClick: () => setOpenDeleteConfirmationModal(true),
                  },
              ]
            : [
                  {
                      disabled: Boolean(
                          tpmTpoActiveTab?.key === 'tpm' ||
                              calenderStatues?.publish_status == 'publish_in_progress' ||
                              (viewMode && planEditMetaData?.editingUser) ||
                              !viewMode ||
                              isYearExpired,
                      ), //if some other user is editing, then disable edit plan button
                      label: <p className="font-xs h-full w-full cursor-pointer">Edit plan</p>,
                      key: edit_plan,
                      onClick: () => {
                          setOpenEditPlanConfirmationModal(true);
                      },
                  },
                  {
                      type: 'divider' as const,
                  },
                  {
                      disabled: tpmTpoActiveTab?.key === 'tpm' || !isCalenderView,
                      label: (
                          <label className="font-xs  cursor-pointer">
                              {showCalenderChanges ? (
                                  <EyeInvisibleOutlined style={{ position: 'relative', top: -3, marginRight: 3 }} />
                              ) : (
                                  <EyeOutlined style={{ position: 'relative', top: -3, marginRight: 3 }} />
                              )}
                              {showCalenderChanges ? 'Hide ' : 'Show '}
                              Changes
                          </label>
                      ),
                      key: show_changes,
                      onClick: () => {
                          setShowCalenderChanges(!showCalenderChanges);
                      },
                  },
                  // TODO: Enable when validate plan is implemented
                  {
                      disabled:
                          showOptimizedPlanCalender ||
                          validatePlanPollingStatus === ValidatePlanPollingStatus.IN_PROGRESS,
                      label: <label className="font-xs cursor-pointer">Validate Plan</label>,
                      key: validate_plan,
                      onClick: () => onValidatePlanClick(),
                  },
                  {
                      disabled: showOptimizedPlanCalender || !hasValidatePlanData,
                      label: <label className="font-xs cursor-pointer">Show Validate Plan Result</label>,
                      key: validate_plan_result,
                      onClick: () => onShowValidatePlanResultClick(),
                  },
                  {
                      disabled: tpmTpoActiveTab?.key === 'tpm' || pollingStatus !== OptimizationPollingStatus.COMPLETED,
                      label: <label className="font-xs  cursor-pointer">Optimization Results</label>,
                      key: optimization_results,
                      onClick: () => setOpenOptimizeResultModal(true),
                  },
                  {
                      disabled: tpmTpoActiveTab?.key === 'tpm' || !showOptimizedPlanCalender || viewMode,
                      label: <label className="font-xs  cursor-pointer">Save as Original</label>,
                      key: set_to_original,
                      onClick: () => {
                          setOpenOptimizationSyncConfirmationModal(true);
                      },
                  },
                  {
                      label: (
                          <div className={`font-xs  ${false ? 'cursor-not-allowed' : 'cursor-pointer'}`}>Save As</div>
                      ),
                      key: duplicate_plan,
                      onClick: () => {
                          setIsCopyModalVisible(true);
                      },
                  },
                  ...(!showOptimizedPlanCalender
                      ? [
                            {
                                type: 'divider' as const,
                            },
                            {
                                disabled:
                                    disablePublish ||
                                    !(
                                        Boolean(selectedPlanInfo?.tpm_last_sync_date) &&
                                        tpmTpoActiveTab?.key !== 'tpm' &&
                                        planEditMetaData?.editingUser &&
                                        !viewMode &&
                                        calenderStatues?.publish_status !== 'publish_in_progress'
                                    ) ||
                                    isInTpmBatchWindow(tpmWindowStart, tpmWindowEnd),
                                label: (
                                    <Tooltip
                                        title={
                                            isInTpmBatchWindow(tpmWindowStart, tpmWindowEnd)
                                                ? `Publish is disabled during TPM batch window (${tpmWindowStart} – ${tpmWindowEnd} ET).`
                                                : undefined
                                        }
                                    >
                                        <label className="font-xs  cursor-pointer">Publish Plan</label>
                                    </Tooltip>
                                ),
                                key: publish_plan,
                                onClick: () => onClickPublishPlan(),
                            },
                            {
                                type: 'divider' as const,
                            },
                        ]
                      : []),
                  {
                      disabled: disableAddEvent || isTpmTab || !selectedPlanInfo?.isTpmPlan,
                      onClick: () => {
                          exportPlan('export_unpublished');
                      },
                      label: <div className={'font-xs cursor-pointer'}>Export Unpublished</div>,
                      key: export_unpublished,
                  },
                  {
                      disabled: disableAddEvent,
                      onClick: () => {
                          exportPlan();
                      },
                      label: (
                          <div className={`font-xs  ${false ? 'cursor-not-allowed' : 'cursor-pointer'}`}>Export</div>
                      ),
                      key: export_calender_view,
                  },
                  {
                      disabled: showOptimizedPlanCalender || viewMode || isTpmTab,
                      onClick: () => {
                          setOpenDeleteAllConfirmationModal(true);
                      },
                      label: (
                          <div className={`font-xs  ${false ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                              Delete All Promotions
                          </div>
                      ),
                      key: delete_all_promos_events,
                  },
                  ...(!showOptimizedPlanCalender
                      ? [
                            {
                                disabled:
                                    disablePlanApproval ||
                                    !(
                                        tpmTpoActiveTab?.key !== 'tpm' &&
                                        planEditMetaData?.editingUser &&
                                        !viewMode &&
                                        calenderStatues?.approval_status !== 'approval_pending' &&
                                        !isYearExpired &&
                                        selectedPlanInfo?.isTpmPlan
                                    ) ||
                                    isInTpmBatchWindow(tpmWindowStart, tpmWindowEnd),
                                label: (
                                    <Tooltip
                                        title={
                                            isInTpmBatchWindow(tpmWindowStart, tpmWindowEnd)
                                                ? `Plan approval request is disabled during TPM batch window (${tpmWindowStart} – ${tpmWindowEnd} ET).`
                                                : undefined
                                        }
                                    >
                                        <label className="font-xs  cursor-pointer">Plan Approval Req</label>
                                    </Tooltip>
                                ),
                                key: send_for_approval,
                                onClick: () => onSendForApproval(),
                            },
                        ]
                      : []),

                  //   {
                  //       disabled: true,
                  //       label: <div className={`font-xs  ${false ? 'cursor-not-allowed' : 'cursor-pointer'}`}>Save</div>,
                  //       key: save_plan,
                  //       onClick: () => setOpenSavePlanConfirmationModal(true),
                  //   },
                  //   {
                  //       type: 'divider' as const,
                  //   },
                  //   {
                  //       disabled: true,
                  //       label: (
                  //           <div className={`font-xs text-redAlt  ${false ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  //               Delete Plan
                  //           </div>
                  //       ),
                  //       key: delete_plan,
                  //       onClick: () => setOpenDeleteConfirmationModal(true),
                  //   },
              ];
        if (
            tpmTpoActiveTab?.key === 'tpo' &&
            !isCurrentViewEventLibrary &&
            isCalenderView &&
            !showOptimizedPlanCalender
        ) {
            items.unshift({
                disabled: isOptimizationInProgress || viewMode,
                label: <label className="font-xs  cursor-pointer">Sync with TPM</label>,
                key: sync_with_tpm,
                onClick: () => {
                    setOpenSyncWithTpmConfirmationModal(true);
                },
            });
        }
        if (!isCalenderView && !isCurrentViewEventLibrary && !isPlanMetricView) {
            items.unshift({
                label: <label className="font-xs  cursor-pointer">Change KPI View</label>,
                key: change_kpi_view,
                onClick: () => {
                    setOpenKpiViewPopup(true);
                },
            });
        }
        return items;
    }, [
        kpiVariable,
        tpmTpoActiveTab,
        disableAddEvent,
        showCalenderChanges,
        isCurrentViewEventLibrary,
        selectedPlanInfo,
        isCalenderView,
        showOptimizedPlanCalender,
        viewMode,
        planEditMetaData,
        isOptimizationInProgress,
        calenderStatues,
        isYearExpired,
        calenderDataLoading,
        hasValidatePlanData,
        validatePlanPollingStatus,
        tpmWindowStart,
        tpmWindowEnd,
    ]);

    return (
        <div className="flex items-center gap-2 justify-between my-2">
            {/* MULTI SELECTION DIMENSION DROPDOWN - hidden in plan summary view */}
            <div>
                {!isPlanSummaryView &&
                    (!isCalenderView ||
                        (isCalenderView &&
                            (!dimensionConfigData?.calendarColumnDropdownList ||
                                dimensionConfigData.calendarColumnDropdownList.length === 0))) && (
                        <SelectWithInfiniteScroller
                            style={{ width: 300 }}
                            mode={'multiple'}
                            placeholder={`Multiple Selection `}
                            maxTagCount={'responsive'}
                            filterOption={false}
                            showArrow
                            tagRender={(props) => {
                                const { label, value, closable, onClose } = props;
                                return (
                                    <Tag
                                        //color="blue"
                                        style={{ marginRight: 3 }}
                                        // don't pass closable, or set it to false
                                        closable={false}
                                    >
                                        {label}
                                    </Tag>
                                );
                            }}
                            showSearch={false}
                            onVisibilityChange={(visible: boolean) => {
                                if (!visible) {
                                    let tempValues = [...dimensionDropdownValues];
                                    if (tempValues?.length === 0) {
                                        setDimensionDropdownValues([
                                            dimensionConfigData?.dimensionDropdownList?.[0]?.value,
                                        ]);
                                        tempValues = [dimensionConfigData?.dimensionDropdownList?.[0]?.value];
                                    }
                                    onDropdownChange({ ...kpiVariable, group_by: tempValues?.map((value) => value) });
                                }
                            }}
                            value={dimensionDropdownValues}
                            onChange={(val, item) => {
                                const reOrderedList = dimensionConfigData?.dimensionDropdownList
                                    ?.filter((item: any) => {
                                        return val?.includes(item?.value);
                                    })
                                    ?.map((listItem: any) => listItem?.value);
                                setDimensionDropdownValues(reOrderedList ?? []);
                            }}
                            options={dimensionConfigData?.dimensionDropdownList ?? []}
                            disabled={planMetaDataLoading || isCalenderView || isCurrentViewEventLibrary}
                        />
                    )}
            </div>
            <div>
                {isCalenderView &&
                    dimensionConfigData?.calendarColumnDropdownList &&
                    dimensionConfigData.calendarColumnDropdownList.length > 0 && (
                        <SelectWithInfiniteScroller
                            style={{ width: 300 }}
                            mode={'multiple'}
                            placeholder={`Select Columns`}
                            maxTagCount={'responsive'}
                            filterOption={false}
                            showArrow
                            tagRender={(props) => {
                                const { label, value, closable, onClose } = props;
                                // Find if this is a mandatory column
                                const isMandatory = dimensionConfigData?.calendarColumnDropdownList?.some(
                                    (item: any) =>
                                        item.value === value &&
                                        MANDATORY_NON_CALENDAR_COLUMN_DATA_KEYS.includes(item.dataKey),
                                );
                                return (
                                    <Tag style={{ marginRight: 3 }} closable={!isMandatory}>
                                        {label}
                                    </Tag>
                                );
                            }}
                            showSearch={false}
                            value={selectedCalendarColumns}
                            onChange={(val: string[]) => {
                                const mandatoryColumnValues =
                                    dimensionConfigData?.calendarColumnDropdownList
                                        ?.filter((item: any) =>
                                            MANDATORY_NON_CALENDAR_COLUMN_DATA_KEYS.includes(item.dataKey),
                                        )
                                        ?.map((item: any) => item.value) || [];

                                const updatedVal = [...new Set([...mandatoryColumnValues, ...val])];

                                const removedMandatory = mandatoryColumnValues.filter(
                                    (mandatoryVal) => !val.includes(mandatoryVal),
                                );
                                if (removedMandatory.length > 0) {
                                    sendNotification('error', 'Event and Promo columns cannot be unselected');
                                }

                                // Update parent state - this won't trigger API calls
                                if (setSelectedCalendarColumns) {
                                    setSelectedCalendarColumns(updatedVal);
                                }
                            }}
                            options={dimensionConfigData.calendarColumnDropdownList.map((item: any) => ({
                                label: item.label,
                                value: item.value,
                            }))}
                            disabled={planMetaDataLoading || isCurrentViewEventLibrary}
                        />
                    )}
                {/* In plan summary view, search is placed on the left side */}
                {/* {isPlanSummaryView && (
                    <SearchBox disabled={false} onSearch={onSearch} search={searchKey} setSearch={setSearchKey} />
                )} */}
            </div>
            <div className="flex items-center w-full justify-end gap-2">
                {/* SEARCHBOX */}

                {/* In non-summary views, search stays on the right side */}
                {!isPlanSummaryView && (
                    <SearchBox disabled={false} onSearch={onSearch} search={searchKey} setSearch={setSearchKey} />
                )}

                {!isPlanSummaryView && !isCalenderView && !isCurrentViewEventLibrary && (
                    <div className="flex gap-1 items-center">
                        {/* ABS/% TOGGLE */}

                        <span className="text-promoColour font-xxs">%</span>
                        <Switch checked={isKpiAbsView} onChange={(checked) => setIsKpiAbsView(checked)} />
                        <span className="text-promoColour font-xxs">Abs</span>
                    </div>
                )}

                {/* {isCalenderView && (
                    <div className="flex gap-1 items-center">
                        <span className="text-promoColour w-max font-xxs">Bump report</span>
                        <Switch checked={enableBumpReport} onChange={(checked) => setEnableBumpReport(checked)} />
                    </div>
                )} */}

                {!isCalenderView && (
                    <>
                        <KPISelector
                            buttonComponent={
                                <Button
                                    onClick={() => {
                                        setIsKpiFilterVisible(!isKpiFilterVisible);
                                    }}
                                    icon={<ProjectOutlined style={{ position: 'relative', top: -3 }} />}
                                    style={{ borderRadius: 4, margin: 0 }}
                                    type="default"
                                >
                                    KPI
                                </Button>
                            }
                            isVisible={isKpiFilterVisible}
                            setIsVisible={setIsKpiFilterVisible}
                            kpiList={kpiFilterDropdownValues}
                            onSubmit={(values) => {
                                const updatedList = kpiFilterDropdownValues?.map((item) => {
                                    return {
                                        ...item,
                                        isSelected: values.includes(item?.value),
                                    };
                                });
                                onDropdownChange({ ...kpiVariable, kpi_list: values });
                                setKpiDropdownValues(updatedList);
                            }}
                            variableName={'KPI'}
                            hideSearch={false}
                            showSelectAll
                        />
                        {/* Hide time period selector for plan summary — it uses a fixed time period */}
                        {eventViewType !== 'plan_summary' && (
                            <CustomSelect
                                placeholder="Time Period"
                                className="dropdown-height dropdown-width "
                                showArrow
                                loading={false}
                                value={selectedKpiTimePeriod}
                                disabled={false}
                                options={timePeriodDropdownList}
                                onChange={(val: any, item: any) => {
                                    setSelectedKpiTimePeriod(item);
                                    onDropdownChange({ ...kpiVariable, time_period: item?.value });
                                }}
                            />
                        )}
                    </>
                )}
                <CustomSelect
                    placeholder="View"
                    className="dropdown-height dropdown-width-alt "
                    showArrow
                    loading={false}
                    value={eventViewType}
                    disabled={false}
                    options={viewDropdownList}
                    onChange={(val: any, item: any) => {
                        trackEventMixpanel(TPO_PLAN_DETAILS_SUB_VIEW_CHANGED, {
                            view_type: item?.label,
                            ...(selectedPlanInfo ?? {}),
                        });
                        setEventViewType(val);
                        setCalenderVariable({});
                        setKpiVariable({});
                        setSearchKey('');
                        onViewDropdownChange();
                        onDropdownChange({ ...kpiVariable, group_by: [] });
                        switch (val) {
                            case 'promo_metrics':
                                updateFilterObject(promoMetricsFilterKeys);
                                break;
                            case 'plan_metrics':
                                updateFilterObject(planMetricsFilterKeys);
                                break;
                            case 'plan_summary':
                                updateFilterObject(
                                    planSummarySubTab === 'ps_tactic_view'
                                        ? psTacticViewFilterKeys
                                        : psProductViewFilterKeys,
                                );
                                break;
                            default:
                                updateFilterObject(calendarFilterKeys);
                                break;
                        }
                    }}
                />
                {/* Hide add event, optimize plan buttons in plan summary view */}
                {!isPlanSummaryView && (
                    <>
                        {/* ADD EVENT */}
                        <BtnDropdown
                            disabled={
                                isOptimizationInProgress ||
                                isTpmTab ||
                                showOptimizedPlanCalender ||
                                disableAddEvent ||
                                viewMode
                            }
                            menuClassName="!rounded-md"
                            icon={<PlusCircleOutlined style={{ fontSize: '17px', lineHeight: '16px' }} />}
                            title={'Add Event'}
                            onChange={({ key }) => {
                                addEventButtonClickHandler(key);
                            }}
                            items={
                                (isCurrentViewEventLibrary
                                    ? addEventDropdownListEventLibrary
                                    : addEventDropdownList) as unknown as MenuProps['items']
                            }
                        />

                        {/* OPTIMIZE PLAN BUTTON */}
                        {!isCurrentViewEventLibrary && (
                            <Tooltip
                                title={
                                    !selectedPlanInfo?.enableOptimization
                                        ? 'No promo library data available for this plan.'
                                        : ''
                                }
                            >
                                <Button
                                    disabled={
                                        !selectedPlanInfo?.enableOptimization ||
                                        isTpmTab ||
                                        showOptimizedPlanCalender ||
                                        viewMode ||
                                        pollingStatus === OptimizationPollingStatus.IN_PROGRESS
                                    }
                                    type="primary"
                                    style={{ borderRadius: 6, fontWeight: 500 }}
                                    onClick={() => {
                                        setOpenOptimizeModal(true);
                                    }}
                                >
                                    Optimize Plan
                                </Button>
                            </Tooltip>
                        )}
                    </>
                )}
                {filterComponent}

                {/* Hide options dropdown in plan summary view */}
                {!isPlanSummaryView && (
                    <CustomDropdownButton
                        disabled={planMetaDataLoading}
                        style={{ width: 'auto' }}
                        trigger={['click']}
                        menu={{
                            style: { borderRadius: 8, padding: 4, width: 'auto' },
                            items: optionDropdownItems,
                        }}
                    ></CustomDropdownButton>
                )}
            </div>
            <OptimizePlanModal
                open={openOptimizeModal}
                onCancel={onClickCancelOptimizePlan}
                onSubmitFinish={(data) => {
                    setOptimizationId(data.data.optimization_id);
                    setOpenOptimizeModal(false);
                    setPollingStatus(OptimizationPollingStatus.IN_PROGRESS);
                }}
                planId={Number(selectedPlanInfo?.planId)}
                planYear={Number(selectedPlanInfo?.planYear)}
                isTpmPlan={selectedPlanInfo?.isTpmPlan || false}
            />
            <ConfirmationModal
                open={openDeleteConfirmationModal}
                onCancel={() => {
                    setOpenDeleteConfirmationModal(false);
                }}
                onSubmitClick={() => {
                    setOpenDeleteConfirmationModal(false);
                }}
                okText={false ? <Spin indicator={antIconDanger} /> : 'Sync'}
                okButtonProps={{ type: 'ghost', danger: true, style: { borderRadius: 6 } }}
                loading={false}
                subTitle={`Are you sure you want to delete ${selectedPlanInfo?.planName}?`}
                title="Delete Plan"
                description="This will delete the complete plan"
            />
            <ConfirmationModal
                open={openEditPlanConfirmationModal}
                onCancel={() => {
                    setOpenEditPlanConfirmationModal(false);
                }}
                onSubmitClick={() => {
                    acquireEditRightsMutation.mutateAsync({ planId: Number(selectedPlanInfo?.planId) });
                }}
                okText={acquireEditRightsMutation.isPending ? <Spin indicator={antIcon} /> : 'Yes'}
                okButtonProps={{ type: 'primary', style: { borderRadius: 6 } }}
                loading={acquireEditRightsMutation.isPending}
                subTitle={`Are you sure you want to acquire edit permission for ${selectedPlanInfo?.planName}?`}
                title="Edit Plan"
                description=""
            />
            <OptimizeResultModal
                open={openOptimizeResultModal}
                setOpenOptimizeResultModal={setOpenOptimizeResultModal}
                optimizedPlanId={optimizedPlanId ?? 0}
            />
            <TPODuplicatePlanModal
                open={isCopyModalVisible}
                onCancel={(e) => setIsCopyModalVisible(false)}
                setOpen={setIsCopyModalVisible}
                acquireEditRightsMutation={acquireEditRightsMutation}
            />
            <ConfirmationModal
                open={openDeleteAllConfirmationModal}
                onCancel={() => {
                    setOpenDeleteAllConfirmationModal(false);
                }}
                onSubmitClick={() => {
                    deleteAllPromotionsMutation.mutateAsync();
                }}
                okText={deleteAllPromotionsMutation.isPending ? <Spin indicator={antIconDanger} /> : 'Delete'}
                okButtonProps={{ type: 'ghost', danger: true, style: { borderRadius: 6 } }}
                loading={deleteAllPromotionsMutation.isPending}
                subTitle={`Are you sure you want to delete all promotions?`}
                title="Delete All Promotions"
                description={'This will delete all the promotions with the events within this plan.'}
            />
            <ValidatePlanDrawer
                open={openValidatePlanDrawer}
                onClose={handleValidatePlanDrawerClose}
                sections={validatePlanSections}
                loading={showValidatePlanLoader && validatePlanPollingStatus === ValidatePlanPollingStatus.IN_PROGRESS}
                error={validatePlanPollingStatus === ValidatePlanPollingStatus.ERROR}
            />
        </div>
    );
};

export default PlanActions;
export const antIconDanger = <LoadingOutlined style={{ fontSize: 18, color: 'red' }} spin />;
